import { StandardInMemoryTable } from "@/components/in-memory-table/StandardInMemoryTable";
import { useSelectedRowSnapshotsStore } from "@/components/query-table/stores/selectedRowSnapshotsStore";
import {
  QueryTableProps,
  useRawTableRows,
} from "@/components/query-table/QueryTable.shared";
import { DataTableFeatures } from "@/types/dataTableSettings";
import { useMemo } from "react";

interface QueryTableSelectedOnlyViewProps<T extends { id: string }> {
  listProps: QueryTableProps<T>;
  tableFeaturesMerged: DataTableFeatures;
}

export function QueryTableSelectedOnlyView<T extends { id: string }>({
  listProps,
  tableFeaturesMerged,
}: QueryTableSelectedOnlyViewProps<T>) {
  const {
    name,
    schema,
    translationKey,
    columnOverrides,
    pathBuilder,
    options,
    children,
    rowSelection,
    setRowSelection,
    getSelection,
    selectionRevision,
    snapshotTableName,
    query,
  } = listProps;

  const rawTableRows = useRawTableRows(query);
  const tableKey = snapshotTableName ?? name;
  const snapshotMap = useSelectedRowSnapshotsStore((s) => s.maps[tableKey]);

  const selectedRows = useMemo(() => {
    if (!getSelection) return [] as T[];
    const selectedIds = getSelection();
    const rowsById = new Map(
      rawTableRows.map((row) => [String(row.id), row] as const),
    );

    const mergedRows = [...selectedIds]
      .map((id) => rowsById.get(id) ?? (snapshotMap?.get(id) as T | undefined))
      .filter((row): row is T => row != null);

    return mergedRows;
  }, [rawTableRows, snapshotMap, getSelection, selectionRevision]);

  return (
    <StandardInMemoryTable<T>
      schema={schema}
      name={name}
      translationKey={translationKey}
      tableFeatures={tableFeaturesMerged}
      columnOverrides={columnOverrides}
      items={selectedRows}
      options={options}
      pathBuilder={pathBuilder}
      rowSelection={rowSelection}
      setRowSelection={setRowSelection}
    >
      {children}
    </StandardInMemoryTable>
  );
}
